import React, { useState } from "react";
import styled from "styled-components";
import { useParams, useHistory } from "react-router-dom";
import Table from "components/widgets/table";
import { Button } from 'antd';
import * as Icon from 'react-feather';

const Container = styled.div`
  padding: 24px;
  width: 100%;
  margin: 0; /* full width dashboard style */
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  font-size: 20px;
  margin: 0 0 4px 0;
  color: #222;
  text-align: left;
`;

const Info = styled.div`
  color: #666;
  margin-bottom: 0;
  font-size: 13px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
`;

const FormRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const TextInput = styled.input`
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
`;

const ActionButton = styled.button`
  background: transparent;
  border: 1px solid #d9d9d9;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 8px;
`;

export default function CoverageDetailsConfig() {
  const history = useHistory();
  const { id: productSystemRef } = useParams();

  const [description, setDescription] = useState("");
  const [items, setItems] = useState([]); // { id: number, description: string }
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const goBack = () => history.goBack();

  const addItem = () => {
    const trimmed = (description || "").trim();
    if (!trimmed) return;
    const nextId = items.length > 0 ? Math.max(...items.map(i => i.id)) + 1 : 1;
    setItems(prev => [...prev, { id: nextId, description: trimmed }]);
    setDescription("");
  };

  const removeItem = (id) => {
    setItems(prev => prev.filter(i => i.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setEditingValue("");
    }
  };

  // wrapper to support table action which passes the field value (id)
  const handleEditById = (id) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    setEditingId(item.id);
    setEditingValue(item.description);
  };

  const handleRemoveById = (id) => {
    removeItem(id);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditingValue(item.description);
  };

  const saveEdit = (id) => {
    const trimmed = (editingValue || "").trim();
    if (!trimmed) return;
    setItems(prev => prev.map(i => i.id === id ? { ...i, description: trimmed } : i));
    setEditingId(null);
    setEditingValue("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  // prepare data for reusable table: add rowNumber for the '#' column
  const tableData = items.map((it, idx) => ({ ...it, rowNumber: idx + 1 }));

  const tableFields = [
    { name: "#" },
    { name: "Description" },
    { name: "Action" },
  ];

  const rowData = [
    { type: "number", field: "rowNumber" },
    { type: "text", field: "description" },
    {
      type: "action",
      actions: [
        { method: handleEditById, field: "id", title: "Edit" },
        { method: handleRemoveById, field: "id", title: "Remove" },
      ],
    },
  ];

  return (
    <Container>
      <HeaderRow>
        <div>
          <Title>Coverage Details Configuration</Title>
          <Info>Product reference: <strong>{productSystemRef || 'N/A'}</strong></Info>
        </div>
        <div>
          <Button onClick={() => history.goBack()} icon={<Icon.ArrowLeft size={14} />}>Back</Button>
        </div>
      </HeaderRow>

      <Card>
        <FormRow>
          <label style={{ minWidth: 80 }}>Description</label>
          <TextInput
            value={editingId ? editingValue : description}
            onChange={e => editingId ? setEditingValue(e.target.value) : setDescription(e.target.value)}
            placeholder="Enter coverage description"
            aria-label="coverage-description"
          />

          {editingId ? (
            <>
              <Button type="primary" onClick={() => saveEdit(editingId)} disabled={!editingValue.trim()}><span style={{ color: '#fff' }}>Save</span></Button>
              <ActionButton onClick={cancelEdit}>Cancel</ActionButton>
            </>
          ) : (
            <Button type="primary" onClick={addItem} disabled={!description.trim()}><span style={{ color: '#fff' }}>Add</span></Button>
          )}
        </FormRow>

        <Table
          title={"Coverages"}
          table_fields={tableFields}
          table_data={tableData}
          row_data={rowData}
          pageSize={10}
        />
       </Card>
     </Container>
   );
 }
